/**
 * Turns the typed refusals `grantPro` / `revokePro` raise into copy an operator can act on. `Apis.Admin`
 * surfaces them top-level through `SeamErrorFilter` (`extensions.code`) — there are no mutation
 * conventions on this host. A refused root field is non-null, so `data` comes back null and Relay raises
 * `onError` with the payload errors on `error.source`; the code may also only be in free text. Pure, so
 * it is unit-tested without a Relay harness. Prior art: `feature-flag-errors.ts`.
 */

export type ProGrantAction = 'grant' | 'revoke';

const KNOWN_CODES = [
    'GRANT_REFUSED_STORE_FUNDED',
    'REVOKE_REFUSED_NOT_GRANTED',
    'SUBSCRIPTION_NOT_FOUND',
] as const;

export type ProGrantErrorCode = (typeof KNOWN_CODES)[number];

const CODE_PATTERN = new RegExp(`\\b(${KNOWN_CODES.join('|')})\\b`);

interface CodeCarrier {
    message?: unknown;
    extensions?: { code?: unknown } | null;
    source?: { errors?: unknown } | null;
}

function codeFromText(text: unknown): ProGrantErrorCode | null {
    return typeof text === 'string' ? ((text.match(CODE_PATTERN)?.[1] as ProGrantErrorCode) ?? null) : null;
}

function codeFromCarrier(carrier: CodeCarrier): ProGrantErrorCode | null {
    return codeFromText(carrier.extensions?.code) ?? codeFromText(carrier.message);
}

/** The first grant/revoke refusal code in a GraphQL error array, a thrown (Relay) error, or one error. */
export function proGrantErrorCode(source: unknown): ProGrantErrorCode | null {
    if (source == null) return null;
    if (Array.isArray(source)) {
        for (const error of source) {
            const code = codeFromCarrier((error ?? {}) as CodeCarrier);
            if (code) return code;
        }
        return null;
    }
    const carrier = source as CodeCarrier;
    return (
        (Array.isArray(carrier.source?.errors) ? proGrantErrorCode(carrier.source.errors) : null) ??
        codeFromCarrier(carrier)
    );
}

const COPY: Record<ProGrantErrorCode, string> = {
    GRANT_REFUSED_STORE_FUNDED:
        'A store subscription still holds this vault — paying, paused, or cancelled but not yet ' +
        "expired — so it can't be comped. Real money always wins.",
    REVOKE_REFUSED_NOT_GRANTED:
        "This vault isn't complimentary — it's funded by a store or Stripe, and revoke can't " +
        'cancel a paying customer.',
    SUBSCRIPTION_NOT_FOUND: 'This vault has no subscription record, so there is nothing to change.',
};

/** Readable copy for a failed grant or revoke — never the raw GraphQL message or code. */
export function proGrantErrorCopy(source: unknown, action: ProGrantAction): string {
    const code = proGrantErrorCode(source);
    if (code) return COPY[code];

    const message = source instanceof Error ? source.message : '';
    if (/\b403\b/.test(message)) {
        return 'Only a named operator can grant or revoke Complimentary Pro. Sign in with your own account.';
    }
    // ⚠️ Never claim nothing changed: the write may have committed before the response was lost, and a
    // blind retry of a grant on a live comp extends it a second time.
    return action === 'grant'
        ? "Couldn't confirm the grant. Check this vault's plan before trying again — " +
          're-granting extends a live comp.'
        : "Couldn't confirm the revoke. Check this vault's plan before trying again.";
}
